import React, { useMemo, useRef, useState } from 'react';
import { SearchDropdown } from '../../../components/shared/ui';
import { useLenderPackageImport } from '../../../hooks';
import {
  LenderPackageResultSummary,
  LenderPackageSelectStep,
} from '../../../components/lender-packages';

interface LenderImportCardProps {
  lenders: ReadonlyArray<{ id: string; name: string }>;
  isLoadingLenders: boolean;
}

/**
 * Multi-step import card for lender packages on the Import page.
 *
 * Step 1 (upload): Select lender + file, preview packages via API.
 * Step 2 (select): Choose which packages to import; existing ones are flagged and
 *   require a replace confirmation before import.
 * Step 3 (done): Display import result summary.
 */
export const LenderImportCard: React.FC<LenderImportCardProps> = ({ lenders, isLoadingLenders }) => {
  const [selectedLenderId, setSelectedLenderId] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const importer = useLenderPackageImport();

  const lenderOptions = useMemo(
    () => lenders.map((l) => ({ id: l.id, label: l.name })),
    [lenders],
  );

  const selectedLenderOption = useMemo(
    () => lenderOptions.find((o) => o.id === selectedLenderId) ?? null,
    [lenderOptions, selectedLenderId],
  );

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    importer.setSelectedFile(event.target.files?.[0] ?? null);
  };

  const handleReset = (): void => {
    setSelectedLenderId('');
    importer.reset();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div>
      <p className="text-sm text-slate-500 mb-0">Upload a CSV file to import lender package and BOM data.</p>

      {importer.step === 'upload' && (
        <div className="mt-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Lender</label>
              <SearchDropdown
                options={lenderOptions}
                value={selectedLenderOption}
                onSelect={(option) => setSelectedLenderId(option?.id ?? '')}
                placeholder={isLoadingLenders ? 'Loading...' : 'Select lender'}
                isLoading={isLoadingLenders}
                disabled={isLoadingLenders}
                inputClassName="!text-sm !py-2"
                dropdownSize="md"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">CSV File (.csv)</label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-1 file:text-xs file:font-medium file:text-slate-700"
              />
            </div>
          </div>
          <button
            onClick={() => void importer.previewPackages(selectedLenderId)}
            disabled={!selectedLenderId || !importer.selectedFile || importer.isLoading}
            className="w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {importer.isLoading ? 'Previewing...' : 'Preview Packages'}
          </button>
        </div>
      )}

      {importer.step === 'select' && (
        <div className="mt-5">
          <LenderPackageSelectStep
            packages={importer.packages}
            selectedKeys={importer.selectedKeys}
            selectedExistingPackages={importer.selectedExistingPackages}
            isLoading={importer.isLoading}
            isReplaceWarningOpen={importer.isReplaceWarningOpen}
            onBack={importer.goToUpload}
            onToggleKey={importer.toggleKey}
            onSelectAll={importer.selectAll}
            onDeselectAll={importer.deselectAll}
            onRequestImport={() => importer.requestImport(selectedLenderId)}
            onConfirmReplace={() => void importer.confirmImport(selectedLenderId)}
            onCloseReplaceWarning={importer.closeReplaceWarning}
          />
        </div>
      )}

      {importer.step === 'done' && importer.result && (
        <div className="mt-5">
          <LenderPackageResultSummary result={importer.result} />
          <button
            onClick={handleReset}
            className="mt-4 w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700"
          >
            Import Another
          </button>
        </div>
      )}
    </div>
  );
};
